import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

/**
 * La rampa de entrada de los dashboards: un bloque no se pinta hecho, se
 * llena. Cuando el elemento entra en pantalla (15 % visible) `p` sube de 0 a
 * 1 en 800 ms con salida cúbica, y las barras y cifras que lo multiplican
 * crecen con él. Una sola vez por montaje: si el usuario vuelve a subir, el
 * bloque ya está lleno.
 *
 *   const { ref, p } = useScrollRamp();
 *   <div ref={ref}> <span style={{ width: `${pct * p}%` }} /> </div>
 *
 * Con `prefers-reduced-motion` la rampa se salta y `p` nace en 1. Y al
 * imprimir (`beforeprint`) termina de golpe: el PDF se hace sin haber hecho
 * scroll, y una gráfica a medio llenar en papel es una gráfica mal.
 *
 * `restartKey`: cuando cambia, la rampa vuelve a empezar. Sirve para los
 * bloques que se montan antes de tener sus datos (Insights pinta la tira de
 * eventos y luego llegan los hechos): sin ella, la rampa corre sobre ceros y
 * las cifras aparecen de golpe después. También re-cuenta al cambiar la
 * selección, como el typewriter vuelve a escribir un texto nuevo.
 */
export function useScrollRamp(durationMs = 800, restartKey?: unknown): { ref: (el: HTMLElement | null) => void; p: number } {
  const [p, setP] = useState(0);
  const started = useRef(false);
  const observer = useRef<IntersectionObserver | null>(null);
  const raf = useRef<number | null>(null);
  const node = useRef<HTMLElement | null>(null);

  const run = useCallback(() => {
    if (started.current) return;
    started.current = true;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setP(1);
      return;
    }
    const t0 = performance.now();
    let last = 0;
    const step = (t: number) => {
      const raw = Math.min(1, (t - t0) / durationMs);
      // ~20 fps basta y no vuelve a pintar el árbol entero a 60 fps.
      if (raw < 1 && t - last < 48) {
        raf.current = requestAnimationFrame(step);
        return;
      }
      last = t;
      setP(1 - Math.pow(1 - raw, 3));
      if (raw < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  }, [durationMs]);

  const observe = useCallback(
    (el: HTMLElement | null) => {
      observer.current?.disconnect();
      observer.current = null;
      if (!el || started.current) return;
      if (typeof IntersectionObserver === 'undefined') {
        run();
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            run();
          }
        },
        { threshold: 0.15 },
      );
      io.observe(el);
      observer.current = io;
    },
    [run],
  );
  const ref = useCallback(
    (el: HTMLElement | null) => {
      node.current = el;
      observe(el);
    },
    [observe],
  );

  // La clave cambió: de cero otra vez, y a observar (o a correr, si ya está a la vista).
  const primeraClave = useRef(true);
  useEffect(() => {
    if (primeraClave.current) {
      primeraClave.current = false;
      return;
    }
    started.current = false;
    if (raf.current != null) cancelAnimationFrame(raf.current);
    setP(0);
    observe(node.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sólo cuando cambia la clave
  }, [restartKey]);

  useEffect(() => {
    const finish = () => {
      started.current = true;
      observer.current?.disconnect();
      if (raf.current != null) cancelAnimationFrame(raf.current);
      // Síncrono: el navegador toma la foto en cuanto vuelve `beforeprint`.
      flushSync(() => setP(1));
    };
    window.addEventListener('beforeprint', finish);
    const mq = window.matchMedia?.('print');
    const onMq = (e: MediaQueryListEvent) => { if (e.matches) finish(); };
    mq?.addEventListener?.('change', onMq);
    return () => {
      window.removeEventListener('beforeprint', finish);
      mq?.removeEventListener?.('change', onMq);
      observer.current?.disconnect();
      if (raf.current != null) cancelAnimationFrame(raf.current);
    };
  }, []);

  return { ref, p };
}

/** Una cifra que cuenta hasta su valor con la rampa. */
export function ramped(value: number | null | undefined, p: number): number | null {
  if (value == null) return null;
  return Math.round(value * p);
}
